<!--
  AI:
  Status: 'in progress'
  Type: Component
  SubType: ProtectedComponent
  Reason: Per-contract "Vertragsabrechnung" — chronological lazy table of the CALCULATED commission records
          (Soll) for one contract, AP/BP/Sonstige provision badges, and full-contract AP/BP/Sonstige/Gesamt
          summary tiles. Read-only; embedded into the CM contract page via @Input() contractId.
-->
@if (initialized) {
    <div class="cn-contract-commission">

        <!-- ===================== KOPFZEILE: TITEL + AKTUALISIEREN ===================== -->
        <div class="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div class="flex align-items-center gap-2 flex-wrap">
                <i class="fa-regular fa-file-invoice-dollar text-xl text-primary"></i>
                <span class="text-lg font-medium">Vertragsabrechnung</span>
            </div>
            <p-button label="Aktualisieren"
                      icon="fa-regular fa-arrows-rotate"
                      severity="secondary"
                      size="small"
                      [outlined]="true"
                      [loading]="listLoading"
                      [disabled]="contractId === undefined || contractId === null"
                      (onClick)="manualRefresh()">
            </p-button>
        </div>

        <!-- ===================== ZUSAMMENFASSUNG (GESAMTER VERTRAG) ===================== -->
        <div class="grid mb-1">
            <div class="col-6 md:col-3">
                <div class="cn-summary-tile surface-0 border-1 surface-border border-round p-3 h-full">
                    <div class="text-sm text-600 mb-1">
                        <i class="fa-regular fa-circle mr-1 text-blue-500"></i>AP (Abschluss)
                    </div>
                    <div class="text-2xl font-bold text-blue-600">
                        {{ sumAp | currency:'EUR':'symbol':'1.2-2':'de' }}
                    </div>
                </div>
            </div>

            <div class="col-6 md:col-3">
                <div class="cn-summary-tile surface-0 border-1 surface-border border-round p-3 h-full">
                    <div class="text-sm text-600 mb-1">
                        <i class="fa-regular fa-circle mr-1 text-pink-500"></i>BP (Bestandspflege)
                    </div>
                    <div class="text-2xl font-bold text-pink-600">
                        {{ sumBp | currency:'EUR':'symbol':'1.2-2':'de' }}
                    </div>
                </div>
            </div>

            <div class="col-6 md:col-3">
                <div class="cn-summary-tile surface-0 border-1 surface-border border-round p-3 h-full">
                    <div class="text-sm text-600 mb-1">
                        <i class="fa-regular fa-circle mr-1 text-500"></i>Sonstige
                    </div>
                    <div class="text-2xl font-bold text-700">
                        {{ sumSonstige | currency:'EUR':'symbol':'1.2-2':'de' }}
                    </div>
                </div>
            </div>

            <div class="col-6 md:col-3">
                <div class="cn-summary-tile surface-50 border-1 surface-border border-round p-3 h-full">
                    <div class="text-sm text-600 mb-1">
                        <i class="fa-regular fa-sigma mr-1"></i>Gesamt
                    </div>
                    <div class="text-2xl font-bold">
                        {{ sumGesamt | currency:'EUR':'symbol':'1.2-2':'de' }}
                    </div>
                </div>
            </div>
        </div>
        <div class="text-xs text-500 mb-3">
            <i class="fa-regular fa-circle-info mr-1"></i>
            Berechnete Provision je Provisionsart (gesamter Vertrag).
        </div>

        <!-- ===================== LISTEN-FEHLER ===================== -->
        @if (listError) {
            <p-message severity="error" class="block mb-3" [text]="listError"></p-message>
        }

        <!-- ===================== ABRECHNUNGS-TABELLE ===================== -->
        <!-- The lazy p-table must ALWAYS render so it can fire its initial (onLazyLoad) and load page 0.
             Loading + empty states are handled by the table itself ([loading] overlay and the
             "emptymessage" template), not by an outer @if that would prevent the first load. -->
        <p-table [value]="rows"
                 styleClass="p-datatable-sm"
                 [scrollable]="true"
                 [lazy]="true"
                 [paginator]="true"
                 [rows]="rowsPerPage"
                 [first]="first"
                 [totalRecords]="totalRecords"
                 [loading]="listLoading"
                 [rowsPerPageOptions]="[25, 50, 100]"
                 [showCurrentPageReport]="true"
                 currentPageReportTemplate="{first}–{last} von {totalRecords}"
                 (onLazyLoad)="onLazyLoad($event)">
            <ng-template pTemplate="header">
                <tr>
                    <th>Buchungsperiode</th>
                    <th>Provisionsart</th>
                    <th class="text-right">Bemessungsbasis</th>
                    <th class="text-right">Satz</th>
                    <th class="text-right">Brutto</th>
                    <th class="text-right">Netto</th>
                    <th>Modell</th>
                    <th>Status</th>
                </tr>
            </ng-template>

            <ng-template pTemplate="body" let-row>
                <tr>
                    <td>{{ row.bookingMonth || '–' }}</td>
                    <td>
                        <p-tag [value]="provisionBadge(row).label"
                               [style]="provisionBadge(row).style"
                               [pTooltip]="provisionTooltip(row)"
                               tooltipPosition="top">
                        </p-tag>
                    </td>
                    <td class="text-right">
                        {{ (row.baseAmount ?? 0) | currency:'EUR':'symbol':'1.2-2':'de' }}
                    </td>
                    <td class="text-right">
                        @if (row.rate !== undefined && row.rate !== null) {
                            {{ row.rate | number:'1.2-2':'de' }} %
                        } @else {
                            –
                        }
                    </td>
                    <td class="text-right">
                        {{ (row.grossAmount ?? 0) | currency:'EUR':'symbol':'1.2-2':'de' }}
                    </td>
                    <td class="text-right">
                        {{ (row.netAmount ?? 0) | currency:'EUR':'symbol':'1.2-2':'de' }}
                    </td>
                    <td>{{ modelLabel(row) }}</td>
                    <td>
                        <p-tag [value]="calculationStatusLabel(row.status)"
                               [severity]="calculationStatusSeverity(row.status)">
                        </p-tag>
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="8" class="text-center text-600 p-4">
                        <i class="fa-regular fa-folder-open text-3xl mb-2 block"></i>
                        @if (contractId === undefined || contractId === null) {
                            Kein Vertrag ausgewählt.
                        } @else {
                            Keine Berechnungen für diesen Vertrag vorhanden.
                        }
                    </td>
                </tr>
            </ng-template>
        </p-table>

    </div>
}
